import dayjs from 'dayjs'
import { useEffect } from 'react'
import utc from 'dayjs/plugin/utc'
import { getCPDataWithoutParams, postCPDataWithoutParams } from 'src/clinic/services'
import { getMPDataWithoutParams } from 'src/mentor/services'
import { LOCALSTORAGE_KEYS_IDB_STORE_LAST_UPDATED } from 'src/consumer/constants'
import { createERMDatabase, deleteERMDatabase, ERM_DB_VERSION } from './indexedDb'
import {
  addOrUpdateAdvicesInIdb,
  addOrUpdateClinicalEntitiesInIdb,
  addOrUpdateDiagnosisInIdb,
  addOrUpdateDrugAdviceEntitiesInIdb,
  addOrUpdateDrugsInIdb,
  addOrUpdateGenericDrugsInIdb,
  addOrUpdateHealthComplaintsInIdb,
  addOrUpdateInstructionsInIdb,
  addOrUpdateInvestigationInIdb,
  addOrUpdateObservationsInIdb,
  addOrUpdatePatientsInIdb,
  addOrUpdatePsychAssessmentsInIdb,
  clearDecryptedStoreCache,
  DRUG_ADVICE_ENTITY_PARAMS_WITH_IDB_STORE,
} from './indexedDbDao'

dayjs.extend(utc)

// Background population of the EMR IndexedDB.
//
// Safety/resilience properties:
// - single-flight per tab, and a Web Locks guard so two tabs never sync concurrently
// - starts when the browser is idle (never competes with initial page render) and
//   waits for connectivity; yields between pages so the UI stays responsive
// - every page fetch retries with exponential backoff before giving up
// - a store's last-synced timestamp advances ONLY after that store fully synced for
//   every clinic brand — a failed run leaves the timestamp behind, so the next run
//   re-covers the missed window instead of silently losing it
// - one store failing never blocks the others; failures are collected and reported
// - re-runs periodically while the app stays open, so local data keeps tracking the
//   server without any user action

const SYNC_LOCK_NAME = 'erm-idb-background-sync'
const PAGE_SIZE = 500
const MAX_PAGE_ATTEMPTS = 3
const RETRY_BASE_DELAY_MS = 1500
const INTER_PAGE_DELAY_MS = 50
const PERIODIC_RESYNC_INTERVAL_MS = 30 * 60 * 1000
const STORAGE_TIME_FORMAT = 'YYYY-MM-DD HH:mm:ss'

const KEYS = LOCALSTORAGE_KEYS_IDB_STORE_LAST_UPDATED

// progressKey values intentionally match the legacy sync-dialog keys
const SYNC_DEFINITIONS = [
  {
    progressKey: 'drugs',
    storageKey: KEYS.DRUGS,
    documentType: 'drugs',
    listPath: '/api/secure/drug/list',
    writeFn: addOrUpdateDrugsInIdb,
    // ~80k legacy public drugs + ~250k A-Z India catalog drugs at PAGE_SIZE 500
    estimatedPages: 700,
  },
  {
    progressKey: 'complaints',
    storageKey: KEYS.COMPLAINTS,
    documentType: 'health_complaints',
    listPath: '/api/secure/clinic-practitioner/health-complaint/list',
    writeFn: addOrUpdateHealthComplaintsInIdb,
    estimatedPages: 50,
  },
  {
    progressKey: 'diagnosis',
    storageKey: KEYS.DIAGNOSIS,
    documentType: 'diagnosis',
    listPath: '/api/secure/clinic-practitioner/diagnosis/list',
    writeFn: addOrUpdateDiagnosisInIdb,
    estimatedPages: 50,
  },
  {
    progressKey: 'observations',
    storageKey: KEYS.OBSERVATIONS,
    documentType: 'health_observations',
    listPath: '/api/secure/clinic-practitioner/health-observation/list',
    writeFn: addOrUpdateObservationsInIdb,
    estimatedPages: 50,
  },
  {
    progressKey: 'advices',
    storageKey: KEYS.ADVICES,
    documentType: 'prescription_advice',
    listPath: '/api/secure/clinic-practitioner/prescription-advice/list',
    writeFn: addOrUpdateAdvicesInIdb,
    estimatedPages: 50,
  },
  {
    progressKey: 'instruction',
    storageKey: KEYS.INSTRUCTIONS,
    documentType: 'instructions',
    listPath: '/api/secure/clinic-practitioner/instruction/list',
    writeFn: addOrUpdateInstructionsInIdb,
    estimatedPages: 50,
  },
  {
    progressKey: 'investigation',
    storageKey: KEYS.INVESTIGATION,
    documentType: 'investigation',
    listPath: '/api/secure/clinic-practitioner/investigation/list',
    writeFn: addOrUpdateInvestigationInIdb,
    estimatedPages: 50,
  },
  {
    progressKey: 'patients',
    storageKey: KEYS.PATIENTS,
    // must match the backend's MedicalDocCollectionUpdateStatus.PATIENT_INFO_COLLECTION_NAME —
    // this was 'patients', which never matched, so the store synced once and then skipped forever
    documentType: 'patient-info-collection-name',
    listPath: '/api/secure/patient/list',
    writeFn: addOrUpdatePatientsInIdb,
    estimatedPages: 50,
  },
  {
    progressKey: 'psychAssessments',
    storageKey: KEYS.PSYCH_ASSESSMENTS,
    documentType: 'psych_assessments',
    listPath: '/api/secure/clinic-practitioner/psych-assessment/list',
    writeFn: addOrUpdatePsychAssessmentsInIdb,
    estimatedPages: 50,
  },
  {
    progressKey: 'genericDrugs',
    storageKey: KEYS.GENERIC_DRUGS,
    documentType: 'drugs_generic_names',
    listPath: '/api/secure/clinic-practitioner/drug-generic-formula/list',
    writeFn: addOrUpdateGenericDrugsInIdb,
    // ~60k legacy formulas + the new A-Z catalog formulas at PAGE_SIZE 500
    estimatedPages: 300,
  },
  // ICD-10 clinical vocabulary — documentType values match the backend's
  // MedicalDocCollectionUpdateStatus constants so custom entries re-sync incrementally
  {
    progressKey: 'clinicalDiagnosis',
    storageKey: KEYS.CLINICAL_DIAGNOSIS,
    documentType: 'clinical_diagnoses',
    listPath: '/api/secure/clinic-practitioner/clinical-diagnosis/list',
    writeFn: (items) => addOrUpdateClinicalEntitiesInIdb('clinical-diagnosis', items),
    estimatedPages: 25,
  },
  {
    progressKey: 'clinicalSymptoms',
    storageKey: KEYS.CLINICAL_SYMPTOMS,
    documentType: 'clinical_symptoms',
    listPath: '/api/secure/clinic-practitioner/clinical-symptom/list',
    writeFn: (items) => addOrUpdateClinicalEntitiesInIdb('clinical-symptom', items),
    estimatedPages: 3,
  },
  {
    progressKey: 'clinicalDescriptors',
    storageKey: KEYS.CLINICAL_DESCRIPTORS,
    documentType: 'clinical_descriptors',
    listPath: '/api/secure/clinic-practitioner/clinical-descriptor/list',
    writeFn: (items) => addOrUpdateClinicalEntitiesInIdb('clinical-descriptor', items),
    estimatedPages: 2,
  },
]

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

function waitForBrowserIdle() {
  return new Promise((resolve) => {
    if (typeof window !== 'undefined' && typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(() => resolve(), { timeout: 8000 })
    } else {
      setTimeout(resolve, 4000)
    }
  })
}

function waitUntilOnline() {
  return new Promise((resolve) => {
    if (typeof navigator === 'undefined' || navigator.onLine !== false) {
      resolve()
      return
    }
    window.addEventListener('online', () => resolve(), { once: true })
  })
}

async function fetchPageWithRetry(url) {
  let lastError
  for (let attempt = 1; attempt <= MAX_PAGE_ATTEMPTS; attempt++) {
    try {
      await waitUntilOnline()
      const response = await getCPDataWithoutParams(url)
      if (response?.status === 200 && Array.isArray(response?.data)) {
        return response.data
      }
      lastError = new Error(`Unexpected response (status ${response?.status}) for ${url}`)
    } catch (error) {
      lastError = error
    }
    if (attempt < MAX_PAGE_ATTEMPTS) {
      await sleep(RETRY_BASE_DELAY_MS * 2 ** (attempt - 1))
    }
  }
  throw lastError
}

async function syncStoreForBrand({ definition, clinicBrandId, modifiedAfter, onProgress }) {
  let page = 1
  let records_total = 0
  while (true) {
    const modifiedAfterParam = modifiedAfter
      ? `&modifiedAfter=${dayjs.utc(modifiedAfter).format('YYYY-MM-DDTHH:mm:ss')}Z`
      : ''
    const url = `${definition.listPath}?search=&clinicBrandId=${clinicBrandId}&page=${page}&pageSize=${PAGE_SIZE}${modifiedAfterParam}`
    const records = await fetchPageWithRetry(url)
    if (records.length === 0) {
      break
    }
    await definition.writeFn(records)
    records_total += records.length
    onProgress?.(
      `${definition.progressKey}-${clinicBrandId}`,
      Math.min((page * 100) / (definition.estimatedPages || 50), 99),
    )
    page += 1
    // breathe between pages so a long first sync never hogs the event loop
    await sleep(INTER_PAGE_DELAY_MS)
  }
  onProgress?.(`${definition.progressKey}-${clinicBrandId}`, 100)
  return { pages: page - 1, records: records_total }
}

/**
 * Server-side last-update times per document type (max across brands). A failed
 * status call leaves the type unknown, which we treat the same way the legacy sync
 * did: skip when we already have local data, sync when we have none.
 */
async function getMongoLastUpdatedByDocumentType(clinicBrandIds) {
  const byType = {}
  await Promise.all(
    clinicBrandIds.map(async (clinicBrandId) => {
      try {
        const response = await getMPDataWithoutParams(
          `/api/secure/doctor/medical-document-update-status/list?clinicBrandId=${clinicBrandId}`,
        )
        if (response?.status === 200 && Array.isArray(response?.data)) {
          response.data.forEach((item) => {
            const lastUpdateTime = item?.lastUpdateTimeUtc
            if (!item?.documentType || !lastUpdateTime) {
              return
            }
            if (
              !byType[item.documentType] ||
              dayjs.utc(lastUpdateTime).isAfter(dayjs.utc(byType[item.documentType]))
            ) {
              byType[item.documentType] = lastUpdateTime
            }
          })
        }
      } catch (error) {
        console.warn('Could not read medical-document-update-status for', clinicBrandId, error)
      }
    }),
  )
  return byType
}

async function runEmrSync({ clinicBrandIds, force, onProgress }) {
  const runStartedAt = Date.now()
  if (force) {
    await deleteERMDatabase()
    clearDecryptedStoreCache()
  }
  // opening the DB first matters: a pending version upgrade wipes the stores and
  // clears the last-updated keys, and that must happen BEFORE we read those keys
  await createERMDatabase()

  const mongoLastByType = force ? {} : await getMongoLastUpdatedByDocumentType(clinicBrandIds)
  const failures = []
  const synced = []
  const skipped = []
  const storeStats = []

  for (const definition of SYNC_DEFINITIONS) {
    const lastSyncedLocal = force ? null : localStorage.getItem(definition.storageKey)
    let modifiedAfter = null
    if (lastSyncedLocal) {
      const mongoLast = mongoLastByType[definition.documentType]
      if (!mongoLast || !dayjs.utc(mongoLast).isAfter(dayjs.utc(lastSyncedLocal))) {
        skipped.push(definition.progressKey)
        continue
      }
      modifiedAfter = lastSyncedLocal
    }

    // captured before fetching so writes that land mid-sync are re-covered next time
    const startedAtUtc = dayjs.utc().format(STORAGE_TIME_FORMAT)
    const storeStartedAt = Date.now()
    let pages = 0
    let records = 0
    try {
      for (const clinicBrandId of clinicBrandIds) {
        const brandResult = await syncStoreForBrand({
          definition,
          clinicBrandId,
          modifiedAfter,
          onProgress,
        })
        pages += brandResult?.pages || 0
        records += brandResult?.records || 0
      }
      localStorage.setItem(definition.storageKey, startedAtUtc)
      synced.push(definition.progressKey)
      storeStats.push({
        store: definition.progressKey,
        status: 'synced',
        mode: modifiedAfter ? 'incremental' : 'full',
        pages,
        records,
        durationMs: Date.now() - storeStartedAt,
      })
    } catch (error) {
      // timestamp deliberately NOT advanced — the next run re-syncs this window
      console.warn(`EMR background sync failed for ${definition.progressKey}`, error)
      failures.push({ store: definition.progressKey, error: error?.message })
      storeStats.push({
        store: definition.progressKey,
        status: 'failed',
        mode: modifiedAfter ? 'incremental' : 'full',
        pages,
        records,
        durationMs: Date.now() - storeStartedAt,
        error: error?.message,
      })
    }
  }

  // Rx dropdown value stores (dosage/duration/frequency/timing/type/notes) — small
  // lists, synced incrementally on every run under one shared timestamp
  const adviceLastSynced = force ? null : localStorage.getItem(KEYS.DRUG_ADVICE_ENTITIES)
  const adviceStartedAtUtc = dayjs.utc().format(STORAGE_TIME_FORMAT)
  try {
    for (const clinicBrandId of clinicBrandIds) {
      for (const entityParam of DRUG_ADVICE_ENTITY_PARAMS_WITH_IDB_STORE) {
        await syncStoreForBrand({
          definition: {
            progressKey: entityParam,
            listPath: `/api/secure/clinic-practitioner/${entityParam}/list`,
            writeFn: (items) => addOrUpdateDrugAdviceEntitiesInIdb(entityParam, items),
            estimatedPages: 2,
          },
          clinicBrandId,
          modifiedAfter: adviceLastSynced,
          onProgress,
        })
      }
    }
    localStorage.setItem(KEYS.DRUG_ADVICE_ENTITIES, adviceStartedAtUtc)
    synced.push('drugAdviceEntities')
  } catch (error) {
    console.warn('EMR background sync failed for drug advice entities', error)
    failures.push({ store: 'drugAdviceEntities', error: error?.message })
    storeStats.push({ store: 'drugAdviceEntities', status: 'failed', error: error?.message })
  }

  const result = {
    synced,
    skipped,
    failures,
    storeStats,
    force,
    durationMs: Date.now() - runStartedAt,
  }
  reportSyncRunToServer(result, clinicBrandIds)
  return result
}

/**
 * Fire-and-forget admin visibility: POSTs the run summary so the server can email the
 * admin alert list (gated by the Alert Communications toggle server-side). Sent only for
 * runs worth knowing about — a full (re)fill, a forced sync, or any failure. Silent
 * periodic no-op/incremental runs stay silent, and reporting errors never affect the sync.
 */
function reportSyncRunToServer(result, clinicBrandIds) {
  try {
    const didFullFill = (result.storeStats || []).some(
      (stat) => stat.mode === 'full' && stat.records > 0,
    )
    const hasFailures = (result.failures || []).length > 0
    if (!result.force && !hasFailures && !didFullFill) {
      return
    }
    const payload = {
      trigger: result.force ? 'manual-force' : didFullFill ? 'initial-or-refill' : 'periodic',
      dbVersion: ERM_DB_VERSION,
      clinicBrandIds,
      durationMs: result.durationMs,
      syncedStores: result.synced,
      skippedStores: result.skipped,
      storeStats: result.storeStats,
      failures: result.failures,
      pageUrl: typeof window !== 'undefined' ? window.location?.pathname : null,
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
    }
    postCPDataWithoutParams('/api/secure/clinic-practitioner/emr-sync/report', payload).catch(
      () => {},
    )
  } catch (error) {
    // reporting must never break the sync itself
    console.warn('Could not report EMR sync run to server', error)
  }
}

let inFlightSync = null

/**
 * Kicks off one background sync run. Safe to call from anywhere, any number of
 * times: concurrent calls in the same tab join the in-flight run, and the Web Locks
 * guard makes a second tab skip instead of double-syncing.
 */
export function startEmrBackgroundSync({
  clinicBrandIds,
  force = false,
  waitForIdleFirst = true,
  onProgress,
} = {}) {
  const brands = [...new Set((clinicBrandIds || []).filter(Boolean))]
  if (brands.length === 0) {
    return Promise.resolve({ skippedRun: 'no-clinic-brands' })
  }
  if (inFlightSync) {
    return inFlightSync
  }
  inFlightSync = (async () => {
    try {
      if (waitForIdleFirst) {
        await waitForBrowserIdle()
      }
      await waitUntilOnline()

      const runner = () => runEmrSync({ clinicBrandIds: brands, force, onProgress })
      if (typeof navigator !== 'undefined' && navigator.locks?.request) {
        return await navigator.locks.request(SYNC_LOCK_NAME, { ifAvailable: true }, async (lock) => {
          if (!lock) {
            return { skippedRun: 'another-tab-is-syncing' }
          }
          return await runner()
        })
      }
      return await runner()
    } catch (error) {
      console.warn('EMR background sync failed; it will retry on the next run', error)
      return { failedRun: true, error: error?.message }
    } finally {
      inFlightSync = null
    }
  })()
  return inFlightSync
}

let periodicResyncHandle = null
let periodicBrandIds = []
let onlineListenerRegistered = false
let pendingFailureRetryHandle = null
let consecutiveFailureRetries = 0
const FAILURE_RETRY_DELAY_MS = 3 * 60 * 1000
const MAX_CONSECUTIVE_FAILURE_RETRIES = 2

/**
 * Resilience: a run that ends with per-store failures schedules ONE early retry
 * (3 min, capped at 2 consecutive) instead of waiting for the 30-min tick; per-store
 * timestamps only advance on full success, so the retry re-covers exactly the missed
 * window. Regaining connectivity also kicks a run immediately.
 */
function scheduleRetryIfRunFailed(runResultPromise) {
  runResultPromise
    ?.then((result) => {
      const hadFailures = (result?.failures?.length || 0) > 0 || result?.failedRun
      if (!hadFailures) {
        consecutiveFailureRetries = 0
        return
      }
      if (
        pendingFailureRetryHandle ||
        consecutiveFailureRetries >= MAX_CONSECUTIVE_FAILURE_RETRIES ||
        periodicBrandIds.length === 0
      ) {
        return
      }
      consecutiveFailureRetries += 1
      pendingFailureRetryHandle = setTimeout(() => {
        pendingFailureRetryHandle = null
        scheduleRetryIfRunFailed(
          startEmrBackgroundSync({ clinicBrandIds: periodicBrandIds, waitForIdleFirst: false }),
        )
      }, FAILURE_RETRY_DELAY_MS)
    })
    .catch(() => {})
}

/**
 * Starts an immediate background sync and keeps re-syncing on an interval while the
 * app stays open, so the local data tracks the server without user action. Repeat
 * calls just refresh the brand list the interval works with. Also retries early after
 * failed runs and re-kicks when the browser comes back online.
 */
export function ensurePeriodicEmrBackgroundSync({ clinicBrandIds } = {}) {
  // union rather than replace: the portal shell registers all brands while a making page
  // (live session / remote check-in) may only know its own — never narrow the list
  periodicBrandIds = [
    ...new Set([...periodicBrandIds, ...(clinicBrandIds || []).filter(Boolean)]),
  ]
  scheduleRetryIfRunFailed(startEmrBackgroundSync({ clinicBrandIds: periodicBrandIds }))
  if (!onlineListenerRegistered && typeof window !== 'undefined') {
    onlineListenerRegistered = true
    window.addEventListener('online', () => {
      scheduleRetryIfRunFailed(
        startEmrBackgroundSync({ clinicBrandIds: periodicBrandIds, waitForIdleFirst: false }),
      )
    })
  }
  if (periodicResyncHandle) {
    return
  }
  periodicResyncHandle = setInterval(() => {
    scheduleRetryIfRunFailed(
      startEmrBackgroundSync({ clinicBrandIds: periodicBrandIds, waitForIdleFirst: false }),
    )
  }, PERIODIC_RESYNC_INTERVAL_MS)
}

/**
 * Mount-point hook for pages that need the local EMR data regardless of which shell
 * rendered them (mentor portal, /l/* live sessions, remote check-in sessions). Idempotent:
 * single-flight + Web Locks + brand-union mean extra callers never duplicate work.
 */
export function useEnsureEmrBackgroundSync(clinicBrandId) {
  useEffect(() => {
    if (clinicBrandId) {
      ensurePeriodicEmrBackgroundSync({ clinicBrandIds: [clinicBrandId] })
    }
  }, [clinicBrandId])
}
